/* eslint-disable unicode-bom */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import moment from 'moment';
import Box from '@mui/material/Box';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import ToggleButtonGroup from '@mui/material/ToggleButtonGroup';
import ToggleButton from '@mui/material/ToggleButton';
import InputAdornment from '@mui/material/InputAdornment';
import Autocomplete from '@mui/material/Autocomplete';
import Tooltip from '@mui/material/Tooltip';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import Edit from '@mui/icons-material/Edit';
import { apiUrl, fetchJson } from '../../apiBase';
import CalendarView from './CalendarView';
import ClassColorLegend from './ClassColorLegend';
// Unified calendar (AllClassesCalendar removed)

const DAYS = ['Pazartesi','Salı','Çarşamba','Perşembe','Cuma','Cumartesi','Pazar'];

function addMinutesToTimeStr(timeStr, minutesToAdd) {
  const [h, m] = String(timeStr || '00:00').split(':').map((n) => parseInt(n || '0', 10));
  const total = h * 60 + m + (parseInt(minutesToAdd || 0, 10) || 0);
  const hh = Math.floor(((total % (24 * 60)) + (24 * 60)) % (24 * 60) / 60);
  const mm = ((total % 60) + 60) % 60;
  const pad = (n) => String(n).padStart(2, '0');
  return `${pad(hh)}:${pad(mm)}`;
}

function LessonForm({ open, onClose, onSave, defaultMinutes, lessons, teachers = [], mode = 'add', initialLesson = null, lockedClass = '', defaultSubject = '', defaultTeacher = '' }) {
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [subject, setSubject] = useState('');
  const [teacher, setTeacher] = useState('');
  const selectableSubjects = Array.from(new Set((lessons || [])
    .map((lesson) => (typeof lesson === 'string' ? lesson : lesson?.name)).filter(Boolean)));
  const subjectOptions = mode === 'edit' && initialLesson?.subject
    ? Array.from(new Set([...selectableSubjects, initialLesson.subject]))
    : selectableSubjects;

  const reset = () => { setStart(''); setEnd(''); setSubject(''); setTeacher(''); };

  const save = () => {
    const selectedSubject = String(subject || '').trim();
    const isValidSubject = subjectOptions.includes(selectedSubject);
  const sStart = normalizeTime(start);
  if (!/^[0-2]?\d:[0-5]\d$/.test(sStart) || !isValidSubject) return;
  const sEndRaw = normalizeTime(end);
  const finalEnd = /^[0-2]?\d:[0-5]\d$/.test(sEndRaw) ? sEndRaw : addMinutesToTimeStr(sStart, defaultMinutes || 40);
  const payload = { start: sStart, end: finalEnd, subject: selectedSubject };
  const selectedTeacher = String(teacher || '').trim();
  if (selectedTeacher) { payload.teacher = selectedTeacher; }
  onSave(payload);
    reset();
  };

  // Normalize time helpers
  const normalizeTime = (raw) => {
    const s = String(raw || '').trim();
    if (!s) return s;
    if (/^\d{1,2}$/.test(s)) {
      const hh = parseInt(s, 10);
      if (hh >= 0 && hh <= 23) return `${String(hh).padStart(2,'0')}:00`;
    }
    if (/^\d{3}$/.test(s)) {
      const hh = Math.floor(parseInt(s, 10) / 100);
      const mm = parseInt(s, 10) % 100;
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
    }
    if (/^\d{4}$/.test(s)) {
      const hh = parseInt(s.slice(0,2), 10);
      const mm = parseInt(s.slice(2), 10);
      if (hh >= 0 && hh <= 23 && mm >= 0 && mm <= 59) return `${String(hh).padStart(2,'0')}:${String(mm).padStart(2,'0')}`;
    }
    return s;
  };

  // Auto-calc end when start changes
  const onChangeStart = (v) => {
    setStart(v);
  };
  const onBlurStart = (v) => {
    const nv = normalizeTime(v);
    setStart(nv);
    if (/^[0-2]?\d:[0-5]\d$/.test(nv)) {
      setEnd(addMinutesToTimeStr(nv, defaultMinutes || 40));
    }
  };

  const findSubjectForTeacher = (teacherName) => {
    const normalizedName = String(teacherName || '').trim().toLocaleLowerCase('tr-TR');
    const selectedTeacher = (teachers || []).find((item) => (
      `${item?.name || ''} ${item?.surname || ''}`.trim().toLocaleLowerCase('tr-TR') === normalizedName
    ));
    const candidate = String(selectedTeacher?.primary_lesson || selectedTeacher?.branch || '').trim();
    return selectableSubjects.includes(candidate) ? candidate : '';
  };

  // Prefill fields when editing
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && initialLesson) {
        setStart(String(initialLesson.start || ''));
        setEnd(String(initialLesson.end || ''));
        setSubject(String(initialLesson.subject || ''));
        setTeacher(String(initialLesson.teacher || ''));
      } else {
        setSubject(selectableSubjects.includes(String(defaultSubject || '')) ? String(defaultSubject) : '');
        setTeacher(String(defaultTeacher || ''));
      }
    } else {
      // reset when dialog closes
      setStart(''); setEnd(''); setSubject(''); setTeacher('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, mode, initialLesson, defaultSubject, defaultTeacher]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>{mode === 'edit' ? 'Dersi Düzenle' : 'Ders Ekle'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {lockedClass && (
            <TextField
              label="Sınıf"
              value={lockedClass}
              disabled
              size="small"
            />
          )}
          <TextField
            label="Başlangıç (HH:MM)"
            value={start}
            onChange={(e) => onChangeStart(e.target.value)}
            onBlur={(e)=> onBlurStart(e.target.value)}
            size="small"
          />
          <TextField
            label={`Bitiş (HH:MM)`}
            value={end}
            onChange={(e) => setEnd(e.target.value)}
            onBlur={(e)=> setEnd(normalizeTime(e.target.value))}
            size="small"
            helperText={`Varsayılan: ${defaultMinutes || 40} dk`}
          />
          <Autocomplete
            options={subjectOptions}
            value={String(subject || '').trim() || null}
            onChange={(_, v) => {
              const next = v || '';
              setSubject(next);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Ders" size="small" placeholder="Ders seçin" />
            )}
          />
          <Autocomplete
            options={Array.from(new Set([...(teachers || []).map((t) => `${t?.name || ''} ${t?.surname || ''}`.trim()).filter(Boolean), String(defaultTeacher || '').trim()].filter(Boolean)))}
            value={String(teacher || '').trim() || null}
            onChange={(_, v) => {
              const nextTeacher = v || '';
              setTeacher(nextTeacher);
              const teacherSubject = findSubjectForTeacher(nextTeacher);
              if (teacherSubject) setSubject(teacherSubject);
            }}
            renderInput={(params) => (
              <TextField {...params} label="Öğretmen" size="small" placeholder="Öğretmen seçin (opsiyonel)" />
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>İptal</Button>
  <Button variant="contained" onClick={save} disabled={!subject || !(/^[0-2]?\d:[0-5]\d$/.test(start))}>{mode === 'edit' ? 'Kaydet' : 'Ekle'}</Button>
      </DialogActions>
    </Dialog>
  );
}

export default function SchedulesManager({ schoolId, onError, onSuccess, user }) {
  const ALL_OPTION = 'Tüm';
  const isTeacher = String(user?.role || '').toLowerCase() === 'teacher';
  const [classes, setClasses] = useState([]);
  const [selectedClass, setSelectedClass] = useState('');
  const didAutoSelect = useRef(false);
  const [schedule, setSchedule] = useState({});
  // Removed allEvents/allColorMap states; events & colors now derived via useMemo to avoid loops
  const [allSchedulesMap, setAllSchedulesMap] = useState({}); // cache per-class base schedule to avoid refetch
  const [multiOverridesByDate, setMultiOverridesByDate] = useState({}); // full overrides (all classes) for current week when ALL selected
  const [multiWeekAnchor, setMultiWeekAnchor] = useState(() => new Date());
  const [dlgDay, setDlgDay] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editDay, setEditDay] = useState('');
  const [editIndex, setEditIndex] = useState(-1);
  const [editInitial, setEditInitial] = useState(null);
  const [view, setView] = useState(() => isTeacher ? 'calendar' : 'list'); // 'list' | 'calendar'
  const isAllSelected = useMemo(() => !isTeacher && selectedClass === ALL_OPTION, [isTeacher, selectedClass]);
  const [calendarView] = useState(() => {
    // Default: week on desktop, day on mobile
    try {
      const ua = typeof navigator !== 'undefined' ? navigator.userAgent || '' : '';
      const mobileUA = /Mobi|Android|iPhone|iPad|iPod/i.test(ua);
      const smallScreen = typeof window !== 'undefined' && window.matchMedia ? window.matchMedia('(max-width: 768px)').matches : false;
      return (mobileUA || smallScreen) ? 'day' : 'week';
    } catch { return 'week'; }
  });
  const [overridesByDate, setOverridesByDate] = useState({});
  const [holidayDate, setHolidayDate] = useState('');
  const [holidayNote, setHolidayNote] = useState('');
  const [holidayDialogOpen, setHolidayDialogOpen] = useState(false);
  const [lastHolidayUpdate, setLastHolidayUpdate] = useState(0);
  const [defaultMinutes, setDefaultMinutes] = useState(() => {
    const saved = Number(localStorage.getItem('scheduleDefaultMinutes') || '40');
    return Number.isFinite(saved) && saved > 0 ? saved : 40;
  });
  const [lessons, setLessons] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [teacherContext, setTeacherContext] = useState(null);
  const availableLessons = isTeacher && Array.isArray(teacherContext?.lessons)
    ? teacherContext.lessons
    : lessons;
  const lockedTeacher = String(teacherContext?.name || user?.name || '').trim() && `${String(teacherContext?.name || user?.name || '').trim()} ${String(teacherContext?.surname || user?.surname || '').trim()}`.trim();
  const defaultSubject = String(teacherContext?.lesson || '').trim();
  const teacherCanEdit = !isTeacher || Boolean(lockedTeacher && defaultSubject);
  const normalizeTeacherName = (value) => String(value || '').trim().toLocaleLowerCase('tr-TR');
  const isOwnLesson = (lesson) => !isTeacher || normalizeTeacherName(lesson?.teacher) === normalizeTeacherName(lockedTeacher);
  const colorMap = useMemo(() => {
    const names = (classes || []).map(c => c?.name).filter(Boolean).sort((a,b)=>String(a).localeCompare(String(b),'tr-TR'));
    const n = Math.max(names.length, 1);
    const map = {};
    names.forEach((name, idx) => {
      const hue = Math.round((idx * 360) / n);
      map[name] = `hsl(${hue}, 65%, 50%)`;
    });
    return map;
  }, [classes]);

  // Cookie-based auth is used; no Authorization header needed
  const fetchWeekOverrides = useCallback(async (anchorDate) => {
    try {
  const base = new Date(anchorDate || new Date());
  const monday = new Date(base); monday.setHours(0,0,0,0);
  const day = monday.getDay(); // 0=Sun,1=Mon,...
  const diff = (day + 6) % 7; // days since Monday
  monday.setDate(monday.getDate() - diff);
      const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' });
      const start = fmt.format(monday);
      const sunday = new Date(monday); sunday.setDate(monday.getDate()+6);
      const end = fmt.format(sunday);
  const r = await fetchJson(
        apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/overrides/range?start=${start}&end=${end}`)
      );
      if (!r.ok) { setOverridesByDate({}); return; }
      const body = await r.json();
      const byDate = (body && body.byDate) || {};
      const next = {};
      Object.entries(byDate).forEach(([d, entry]) => {
  const classesMap = (entry && entry.classes) || {};
  if (Array.isArray(classesMap['*'])) { next[d] = { cancelAll: true, note: entry?.note }; return; }
  const arr = classesMap[selectedClass];
  if (Array.isArray(arr)) { next[d] = { lessons: arr, note: entry?.note }; }
      });
      setOverridesByDate(next);
    } catch(e) { /* ignore fetch failures */ }
  }, [schoolId, selectedClass]);
  useEffect(() => { if (view==='calendar' && !isAllSelected && selectedClass) { fetchWeekOverrides(new Date()); } }, [view, isAllSelected, selectedClass, fetchWeekOverrides]);

  const loadClasses = useCallback(async () => {
    try {
  const res = await fetchJson(
      apiUrl(isTeacher
        ? `/admin/classes/${encodeURIComponent(schoolId)}`
        : `/admin/classes/${encodeURIComponent(schoolId)}/manage`)
    );
      if (!res.ok) throw new Error('classes');
      const list = await res.json();
      const sorted = (list || []).sort((a,b)=> String(a?.name||'').localeCompare(String(b?.name||''), 'tr-TR'));
      setClasses(sorted);
      // On initial load, select the first class once. Do not auto-select again after user clears.
      if (!selectedClass && sorted && sorted.length && !didAutoSelect.current) {
        setSelectedClass(sorted[0].name);
        didAutoSelect.current = true;
      }
    } catch (e) {
      onError && onError('Şubeler yüklenemedi');
    }
  }, [onError, schoolId, selectedClass, isTeacher]);

  // Reset auto-select flag when school changes
  useEffect(() => { didAutoSelect.current = false; }, [schoolId]);

  const loadSchedule = useCallback(async (clsName) => {
    if (!clsName) return;
    try {
  const res = await fetchJson(apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/${encodeURIComponent(clsName)}`));
      if (!res.ok) throw new Error('schedule');
      const data = await res.json();
      setSchedule(data || {});
    } catch (e) {
      onError && onError('Ders programı yüklenemedi');
    }
  }, [onError, schoolId]);

  useEffect(() => { if (schoolId) loadClasses(); }, [schoolId, loadClasses]);
  useEffect(() => { if (selectedClass && !isAllSelected) loadSchedule(selectedClass); }, [selectedClass, isAllSelected, loadSchedule]);
  // Fetch base schedules for classes (cache) when entering ALL mode or classes list changes.
  useEffect(() => {
    if (!isAllSelected || !classes?.length) return;
    let cancelled = false;
    (async () => {
      const toFetch = classes.map(c=>c?.name).filter(Boolean).filter(n => !allSchedulesMap[n]);
      if (!toFetch.length) return;
      const newEntries = {};
      for (const name of toFetch) {
        try {
          const res = await fetchJson(apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/${encodeURIComponent(name)}`));
          if (res.ok) {
            const json = await res.json();
            // Backend may return { schedule: {...days} } or direct day map
            newEntries[name] = json?.schedule || json || {};
          }
        } catch {}
      }
      if (!cancelled && Object.keys(newEntries).length) {
        setAllSchedulesMap(prev => ({ ...prev, ...newEntries }));
      }
    })();
    return () => { cancelled = true; };
  }, [isAllSelected, classes, schoolId, allSchedulesMap]);

  const allColorMap = useMemo(() => {
    const names = (classes || []).map(c=>c?.name).filter(Boolean);
    const n = Math.max(names.length,1);
    const map = {};
    names.forEach((nme,i)=>{ const hue=Math.round((i*360)/n); map[nme]=`hsl(${hue},65%,50%)`; });
    return map;
  }, [classes]);

  const allEventsMemo = useMemo(() => {
    if (!isAllSelected) return [];
    try {
      const base = new Date(multiWeekAnchor || new Date());
      const monday = new Date(base); monday.setHours(0,0,0,0); const day = monday.getDay(); const diff = (day + 6) % 7; monday.setDate(monday.getDate()-diff);
      const startWeek = moment(monday).startOf('isoWeek');
      // ISO weekday: Monday=1 ... Sunday=7
      const DAY_MAP_ISO = { 'Pazartesi':1,'Salı':2,'Çarşamba':3,'Perşembe':4,'Cuma':5,'Cumartesi':6,'Pazar':7 };
      let evs = [];
      Object.entries(allSchedulesMap).forEach(([className, sch]) => {
        const dayMap = sch?.schedule ? sch.schedule : sch; // normalize
        if (!dayMap || typeof dayMap !== 'object') return;
        Object.entries(dayMap).forEach(([dayName, lessons]) => {
          const dow = DAY_MAP_ISO[dayName];
          if (!dow) return; // skip unknown keys
          const baseDay = startWeek.clone().isoWeekday(dow);
          if (Array.isArray(lessons)) {
            lessons.forEach((l, idx) => {
              const [sh,sm] = String(l.start||'00:00').split(':').map(Number);
              const [eh,em] = String(l.end||'00:00').split(':').map(Number);
              const start = baseDay.clone().hour(sh).minute(sm).toDate();
              const end = baseDay.clone().hour(eh).minute(em).toDate();
              evs.push({ id:`${className}-${dayName}-${idx}`, title:`${className} • ${l.subject||''}${l.teacher?` (${l.teacher})`:''}`, start, end, meta:{ className, teacher:l.teacher||'' } });
            });
          }
        });
      });
      Object.entries(multiOverridesByDate || {}).forEach(([dateIso, entry]) => {
        const classesMap = entry?.classes || {};
        const dayStart = moment(dateIso).startOf('day');
        const dayEnd = moment(dateIso).endOf('day');
        if (Array.isArray(classesMap['*'])) {
          evs = evs.filter(ev => !moment(ev.start).isBetween(dayStart, dayEnd, undefined, '[]'));
          const note = entry?.note && String(entry.note).trim();
          evs.push({ id:`holiday-${dateIso}-all`, title: note || '—', start: dayStart.toDate(), end: dayEnd.toDate(), meta:{ holiday:true, dateIso, global:true } });
          return;
        }
        Object.entries(classesMap).forEach(([cls, arr]) => {
          evs = evs.filter(ev => !(moment(ev.start).isBetween(dayStart, dayEnd, undefined, '[]') && ev.meta?.className === cls));
          if (Array.isArray(arr) && arr.length === 0) {
            evs.push({ id:`holiday-${dateIso}-${cls}`, title: cls, start: dayStart.toDate(), end: dayEnd.toDate(), meta:{ holiday:true, dateIso, className: cls } });
          } else if (Array.isArray(arr) && arr.length > 0) {
            arr.forEach((l, idx) => {
              const [sh,sm] = String(l.start||'00:00').split(':').map(Number);
              const [eh,em] = String(l.end||'00:00').split(':').map(Number);
              const start = dayStart.clone().hour(sh).minute(sm).toDate();
              const end = dayStart.clone().hour(eh).minute(em).toDate();
              evs.push({ id:`ov-${dateIso}-${cls}-${idx}`, title:`${cls} • ${l.subject||''}${l.teacher?` (${l.teacher})`:''}`, start, end, meta:{ className: cls, teacher:l.teacher||'', override:true } });
            });
          }
        });
      });
      return evs;
    } catch (e) {
      console.error('allEventsMemo build failed', e);
      return [];
    }
  }, [isAllSelected, multiWeekAnchor, allSchedulesMap, multiOverridesByDate]);

  // Fetch multi-class overrides for the visible week (once per week navigation)
  useEffect(() => {
    if (!isAllSelected || !schoolId) return;
    let cancelled = false;
    (async () => {
      try {
        const base = new Date(multiWeekAnchor || new Date());
        const monday = new Date(base); monday.setHours(0,0,0,0); const day = monday.getDay(); const diff = (day + 6) % 7; monday.setDate(monday.getDate()-diff);
        const fmt = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year:'numeric', month:'2-digit', day:'2-digit' });
        const start = fmt.format(monday);
        const sunday = new Date(monday); sunday.setDate(monday.getDate()+6);
        const end = fmt.format(sunday);
  const r = await fetchJson(
        apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/overrides/range?start=${start}&end=${end}`)
      );
        if (!r.ok) { if (!cancelled) setMultiOverridesByDate({}); return; }
        const body = await r.json();
        const byDate = (body && body.byDate) || {};
        if (!cancelled) setMultiOverridesByDate(byDate);
      } catch { if (!cancelled) setMultiOverridesByDate({}); }
    })();
    return () => { cancelled = true; };
  }, [isAllSelected, schoolId, multiWeekAnchor, lastHolidayUpdate]);

  useEffect(() => { try { localStorage.setItem('scheduleDefaultMinutes', String(defaultMinutes)); } catch(e) {} }, [defaultMinutes]);
  useEffect(() => {
    let cancelled = false;
    const loadLessons = async () => {
      try {
        const classQuery = selectedClass && !isAllSelected
          ? `?class_name=${encodeURIComponent(selectedClass)}`
          : '';
        const r = await fetchJson(apiUrl(`/admin/lessons/${encodeURIComponent(schoolId)}${classQuery}`));
        if (!r.ok) throw new Error('lessons');
        const list = await r.json();
        if (!cancelled) setLessons(list || []);
      } catch(e) {
        if (!cancelled) setLessons([]);
      }
    };
    setLessons([]);
    if (schoolId) loadLessons();
    return () => { cancelled = true; };
  }, [schoolId, selectedClass, isAllSelected]);

  useEffect(() => {
    const loadTeachers = async () => {
        if (isTeacher) return;
      try {
  const r = await fetchJson(apiUrl(`/admin/teachers/${encodeURIComponent(schoolId)}`));
        if (!r.ok) throw new Error('teachers');
        const list = await r.json();
        setTeachers(Array.isArray(list) ? list : (list?.teachers || []));
      } catch (e) {
        // ignore; teacher selection stays empty
      }
    };
    if (schoolId) loadTeachers();
  }, [schoolId, isTeacher]);

  useEffect(() => {
    if (!isTeacher || !schoolId || !selectedClass) {
      setTeacherContext(null);
      return;
    }
    setTeacherContext(null);
    setTeachers([]);
    let cancelled = false;
    (async () => {
      try {
        const response = await fetchJson(apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/teacher-context?class_name=${encodeURIComponent(selectedClass)}`));
        if (!response.ok) throw new Error('teacher-context');
        const context = await response.json();
        if (!cancelled) {
          setTeacherContext(context || null);
          setTeachers(Array.isArray(context?.teachers) ? context.teachers : []);
        }
      } catch (error) {
        if (!cancelled) {
          setTeacherContext(null);
          setTeachers([]);
          onError && onError('Öğretmen bilgileri yüklenemedi');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [schoolId, selectedClass, isTeacher, onError]);

  const openAdd = (day) => { setDlgDay(day); setDlgOpen(true); };

  const personalSchedule = (nextSchedule, previousSchedule) => Object.entries(nextSchedule || {}).reduce((result, [day, dayLessons]) => {
    const previousOtherLessons = (previousSchedule?.[day] || []).filter((lesson) => !isOwnLesson(lesson));
    result[day] = (dayLessons || []).filter((lesson) => {
      if (isOwnLesson(lesson)) return true;
      return !previousOtherLessons.some((previous) => (
        previous.start === lesson.start
        && previous.end === lesson.end
        && previous.subject === lesson.subject
        && String(previous.teacher || '') === String(lesson.teacher || '')
      ));
    });
    return result;
  }, {});

  const saveSchedule = async (nextSchedule, successMessage) => {
    setSchedule(nextSchedule);
    const endpoint = isTeacher
      ? `/admin/schedules/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedClass)}/teacher`
      : `/admin/schedules/${encodeURIComponent(schoolId)}/${encodeURIComponent(selectedClass)}`;
    const response = await fetchJson(apiUrl(endpoint), {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ schedule: isTeacher ? personalSchedule(nextSchedule, schedule) : nextSchedule })
    });
    if (!response.ok) throw new Error('save');
    try {
      const saved = await response.json();
      if (saved && typeof saved === 'object') setSchedule(saved);
    } catch (_) {
      // A successful write does not require a response body.
    }
    onSuccess && onSuccess(successMessage);
  };

  const addLesson = async (lesson) => {
    try {
      // Optimistically update
      const next = { ...schedule, [dlgDay]: [...(schedule[dlgDay] || []), lesson] };
      setDlgOpen(false);
      await saveSchedule(next, 'Ders eklendi');
    } catch (e) { onError && onError('Ders eklenemedi'); }
  };

  const removeLesson = async (day, idx) => {
    try {
      const nextDay = (schedule[day] || []).filter((_, i) => i !== idx);
      const next = { ...schedule, [day]: nextDay };
      await saveSchedule(next, 'Ders silindi');
    } catch (e) { onError && onError('Ders silinemedi'); }
  };

  const openEdit = (day, idx) => {
    try {
      const item = (schedule?.[day] || [])[idx];
      if (!item) return;
      setEditDay(day);
      setEditIndex(idx);
      setEditInitial(item);
      setEditOpen(true);
    } catch {}
  };

  const updateLesson = async (day, idx, lesson) => {
    try {
      const dayArr = [...(schedule[day] || [])];
      dayArr[idx] = lesson;
      const next = { ...schedule, [day]: dayArr };
      setEditOpen(false);
      await saveSchedule(next, 'Ders güncellendi');
    } catch (e) { onError && onError('Ders güncellenemedi'); }
  };

  // Save overrides for a specific class based on byDate mapping coming from CalendarView
  const saveClassOverrides = async (byDate) => {
    if (!selectedClass) return;
    try {
      const DAY_NAME_TR = ['Pazar','Pazartesi','Salı','Çarşamba','Perşembe','Cuma','Cumartesi'];
      const payloads = [];
      for (const [dateIso, arrRaw] of Object.entries(byDate || {})) {
        if (!Array.isArray(arrRaw)) continue;
        // Skip if array represents accidental full-day placeholder (00:00-23:59 subject '—')
        if (arrRaw.length === 1 && arrRaw[0]?.start === '00:00' && arrRaw[0]?.end === '23:59') continue;
        // Determine base schedule for that weekday to diff
        let baseLessons = [];
        try {
          const d = new Date(dateIso + 'T00:00:00');
          const dayName = DAY_NAME_TR[d.getDay()];
          baseLessons = (schedule?.[dayName] || []).map(l => ({ start:l.start, end:l.end, subject:l.subject, teacher:l.teacher }));
        } catch {}
        const cleaned = arrRaw.map(l => ({ start:l.start, end:l.end, subject:l.subject, teacher:l.teacher }));
        // If override identical to base schedule, skip persisting (no real change)
        const sameLen = cleaned.length === baseLessons.length;
        const identical = sameLen && cleaned.every((l,i)=> l.start===baseLessons[i].start && l.end===baseLessons[i].end && l.subject===baseLessons[i].subject && (l.teacher||'')===(baseLessons[i].teacher||''));
        if (identical) continue;
        // If there is a global holiday ('*') for this date, skip (cannot override holiday here)
        // multiOverridesByDate holds global data when ALL selected; for safety also check arrRaw special marker
        const globalEntry = multiOverridesByDate?.[dateIso]?.classes?.['*'];
        if (Array.isArray(globalEntry)) continue;
        // Persist only the changed lessons
        payloads.push([dateIso, cleaned]);
      }
      for (const [dateIso, arr] of payloads) {
        await fetchJson(
          apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/overrides/date/${dateIso}`),
          {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ classes: { [selectedClass]: arr } })
          }
        );
      }
      // Merge into local overrides state for immediate feedback
      setOverridesByDate((prev) => {
        const next = { ...prev };
        payloads.forEach(([dateIso, arr]) => { next[dateIso] = { lessons: arr }; });
        // preserve any existing notes if present
        payloads.forEach(([dateIso]) => {
          if (prev[dateIso]?.note) next[dateIso].note = prev[dateIso].note;
        });
        return next;
      });
      onSuccess && onSuccess('Değişiklikler kaydedildi');
    } catch (e) {
      onError && onError('Değişiklikler kaydedilemedi');
    }
  };


  return (
    <>
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Paper sx={{ p: 3, borderRadius: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2, gap: 2, flexWrap: 'wrap' }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>Ders Programı</Typography>
              <Autocomplete
                size="small"
                sx={{ minWidth: 260 }}
                options={[...(isTeacher ? [] : [ALL_OPTION]), ...(classes || []).map(c => c?.name).filter(Boolean)]}
                value={(() => {
                  const opts = [...(isTeacher ? [] : [ALL_OPTION]), ...(classes || []).map(c => c?.name).filter(Boolean)];
                  return opts.includes(selectedClass) ? selectedClass : null;
                })()}
                onChange={(_, v) => {
                  const next = v || '';
                  setSelectedClass(next);
                  if (next === ALL_OPTION && view !== 'calendar') setView('calendar');
                }}
                clearOnEscape
                renderInput={(params) => (
                  <TextField {...params} label="Şube" placeholder="Yazdıkça filtrelenir" />
                )}
              />
              <TextField
                label="Süre (dk)"
                type="number"
                size="small"
                value={defaultMinutes}
                onChange={(e) => {
                  const v = Number(e.target.value || '0');
                  if (Number.isFinite(v) && v > 0 && v <= 240) setDefaultMinutes(v);
                }}
                sx={{ width: 120 }}
                InputProps={{ endAdornment: <InputAdornment position="end">dk</InputAdornment>, inputProps: { min: 10, max: 240, step: 5 } }}
              />
              <ToggleButtonGroup
                value={view}
                exclusive
                onChange={(_, v) => v && setView(v)}
                size="small"
              >
                <ToggleButton value="list">Liste</ToggleButton>
                <ToggleButton value="calendar">Takvim</ToggleButton>
              </ToggleButtonGroup>
              {/* Sınıf/Tümü toggle removed; use the Sınıf seçim kutusu with 'Tüm' instead */}
              {/* Built-in calendar view switcher (Gün/Hafta) is used; no explicit toggle needed */}
              {/* Per-dialog tek seferlik seçimi; global mod kaldırıldı */}
              {view === 'calendar' && !isTeacher && (
                <Tooltip title={isAllSelected ? 'Bu günü tüm şubeler için tatil yap' : "Sadece 'Tüm' seçiliyken kullanılabilir"} arrow>
                  <span>
                    <Button
                      variant="outlined"
                      color="primary"
                      disabled={!isAllSelected}
                      onClick={() => {
                        if (!isAllSelected) return;
                        try {
                          const today = new Date();
                          const isoTR = new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Istanbul', year: 'numeric', month: '2-digit', day: '2-digit' }).format(today);
                          setHolidayDate((prev) => prev || isoTR);
                        } catch {}
                        setHolidayNote('');
                        setHolidayDialogOpen(true);
                      }}
                    >
                      Günü Tatil Yap
                    </Button>
                  </span>
                </Tooltip>
              )}
            </Box>
            {isAllSelected && !!(classes||[]).length && (
              <ClassColorLegend classes={classes} colorMap={allColorMap} showDivider />
            )}
            {view === 'calendar' && !isAllSelected && (
              <Box sx={{ mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  {isTeacher
                    ? 'Ders ve öğretmen varsayılan olarak sizin bilgilerinizle gelir; sınıfınızdaki başka öğretmenleri de seçebilirsiniz.'
                    : 'Görüntülenen haftanın değişiklikleri otomatik yüklenir.'}
                </Typography>
              </Box>
            )}
  {view === 'list' ? (
              <Grid container spacing={2}>
                {DAYS.map((day) => (
                  <Grid item xs={12} md={6} lg={4} key={day}>
                    <Paper variant="outlined" sx={{ p: 2, minHeight: 160 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{day}</Typography>
                        <Button size="small" startIcon={<Add />} disabled={!teacherCanEdit || !selectedClass} onClick={() => openAdd(day)}>Ders Ekle</Button>
                      </Box>
                      <Stack spacing={1}>
                        {(schedule[day] || []).map((l, i) => (
                          <Box key={`${day}-${i}`} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', border: '1px solid', borderColor: 'divider', borderRadius: 1, px: 1, py: 0.75, transition:'background-color .15s', '&:hover': { backgroundColor: '#F9FAFB' } }}>
                            <Typography variant="body2">{l.start}-{l.end} {l.subject}{l.teacher ? ` • ${l.teacher}` : ''}</Typography>
                            {(!isTeacher || (teacherCanEdit && isOwnLesson(l))) && (
                              <Box>
                                <IconButton aria-label="Dersi düzenle" size="small" onClick={() => openEdit(day, i)} sx={{ mr: 0.5 }}><Edit fontSize="small" /></IconButton>
                                <IconButton aria-label="Dersi sil" size="small" onClick={() => removeLesson(day, i)}><Delete fontSize="small" /></IconButton>
                              </Box>
                            )}
                          </Box>
                        ))}
                        {!(schedule[day] || []).length && (
                          <Typography variant="body2" color="text.secondary">Henüz ders eklenmedi</Typography>
                        )}
                      </Stack>
                    </Paper>
                  </Grid>
                ))}
              </Grid>
  ) : !isAllSelected ? (
  <CalendarView
                schedule={schedule}
                readOnly={isTeacher && (!teacherCanEdit || !selectedClass)}
                overridesByDate={overridesByDate}
                defaultMinutes={defaultMinutes}
    lessons={(availableLessons || []).map((l) => (typeof l === 'string' ? l : l?.name)).filter(Boolean)}
    teachers={teachers}
  calendarView={calendarView}
        lockedClass={isTeacher ? selectedClass : ''}
        lockedTeacher={isTeacher ? lockedTeacher : ''}
        defaultSubject={isTeacher ? defaultSubject : ''}
        defaultTeacher={isTeacher ? lockedTeacher : ''}
        allowOneOff={!isTeacher}
        classColor={selectedClass ? colorMap[selectedClass] : null}
        onChange={async (next) => {
                  try {
                    await saveSchedule(next, 'Ders programı kaydedildi');
                  } catch (e) {
                    onError && onError('Ders programı kaydedilemedi');
                  }
        }}
  onChangeByDate={isTeacher ? undefined : saveClassOverrides}
        onWeekChange={(date) => fetchWeekOverrides(date)}/>
            ) : (
              <CalendarView
                schedule={{}}
                eventsOverride={allEventsMemo}
                readOnly
                overridesByDate={{}}
                defaultMinutes={defaultMinutes}
                lessons={(lessons || []).map((l) => (typeof l === 'string' ? l : l?.name)).filter(Boolean)}
                teachers={teachers}
                calendarView={calendarView}
                colorMap={allColorMap}
                onWeekChange={(d) => setMultiWeekAnchor(d)}
                onRemoveGlobalHoliday={async (dateIso) => {
                  try {
                    await fetchJson(apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/overrides/date/${dateIso}`), { method:'DELETE' });
                    setMultiOverridesByDate(prev => { const next={...prev}; delete next[dateIso]; return next; });
                    setLastHolidayUpdate(Date.now());
                    onSuccess && onSuccess('Tüm şubeler için tatil kaldırıldı');
                  } catch(e){ onError && onError('Tatil kaldırılamadı'); }
                }}
              />
            )}
          </Paper>
        </Grid>
      </Grid>

  <LessonForm open={dlgOpen} onClose={() => setDlgOpen(false)} onSave={addLesson} defaultMinutes={defaultMinutes} lessons={availableLessons} teachers={teachers} lockedClass={isTeacher ? selectedClass : ''} defaultSubject={isTeacher ? defaultSubject : ''} defaultTeacher={isTeacher ? lockedTeacher : ''} />
  <LessonForm open={editOpen} onClose={() => setEditOpen(false)} onSave={(lesson) => updateLesson(editDay, editIndex, lesson)} defaultMinutes={defaultMinutes} lessons={availableLessons} teachers={teachers} mode="edit" initialLesson={editInitial} lockedClass={isTeacher ? selectedClass : ''} defaultSubject={isTeacher ? defaultSubject : ''} defaultTeacher={isTeacher ? lockedTeacher : ''} />
      {/* Holiday date picker dialog */}
      <Dialog open={!!holidayDialogOpen} onClose={() => setHolidayDialogOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Günü Tatil Yap</DialogTitle>
        <DialogContent dividers>
          <TextField
            label="Tarih"
            type="date"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={holidayDate || ''}
            onChange={(e) => setHolidayDate(e.target.value)}
          />
          <TextField
            sx={{ mt: 2 }}
            label="Not"
            type="text"
            fullWidth
            placeholder="Örn. Tatil"
            value={holidayNote}
            onChange={(e) => setHolidayNote(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHolidayDialogOpen(false)}>Vazgeç</Button>
          <Button variant="contained" color="warning" onClick={async () => {
            if (!holidayDate) { onError && onError('Tarih seçin'); return; }
            if (!selectedClass) { onError && onError('Önce bir şube seçin'); return; }
            try {
              if (selectedClass !== ALL_OPTION) { onError && onError('Tatil sadece Tüm seçiliyken oluşturulabilir'); return; }
              const classesMap = { '*': [] }; // wildcard all-classes holiday
              const payload = { classes: classesMap };
              if (holidayNote && holidayNote.trim()) payload.note = holidayNote.trim();
              const res = await fetchJson(apiUrl(`/admin/schedules/${encodeURIComponent(schoolId)}/overrides/date/${holidayDate}`), {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
              });
              if (!res.ok) throw new Error('failed');
              // Update multi overrides cache optimistically
              setMultiOverridesByDate(prev => ({ ...prev, [holidayDate]: { classes: classesMap, ...(holidayNote.trim()? { note: holidayNote.trim() } : {}) } }));
              setLastHolidayUpdate(Date.now());
              onSuccess && onSuccess('Gün tatil olarak kaydedildi');
              setHolidayDialogOpen(false);
            } catch (e) { onError && onError('Tatil kaydedilemedi'); }
          }}>Kaydet</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
